
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Lock, User, Building, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { registerSchema, useStudentAuth } from "@/hooks/useStudentAuth";
import { z } from "zod";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/components/LanguageContext";

type StudentRegisterFormProps = {
  logoUrl: string;
};

export const StudentRegisterForm = ({ logoUrl }: StudentRegisterFormProps) => {
  const { isSubmitting, handleRegister } = useStudentAuth();
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  
  const form = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      country: "",
      city: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof registerSchema>) => {
    await handleRegister(values);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center space-y-4">
        <img 
          src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" 
          alt="Logo" 
          className="h-20 w-auto"
        />
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('auth.student.register.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.student.register.subtitle')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.student.register.firstNameLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input {...field} className="pl-10" placeholder={t('auth.student.register.firstNameLabel')} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.student.register.lastNameLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input {...field} className="pl-10" placeholder={t('auth.student.register.lastNameLabel')} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.student.register.emailLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      type="email"
                      className="pl-10"
                      placeholder={t('auth.student.register.emailPlaceholder')}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.student.register.passwordLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      type="password"
                      className="pl-10"
                      placeholder={t('auth.student.register.passwordPlaceholder')}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.student.register.countryLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Flag className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        className="pl-10"
                        placeholder={t('auth.student.register.countryPlaceholder')}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.student.register.cityLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        className="pl-10"
                        placeholder={t('auth.student.register.cityPlaceholder')}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {t('auth.student.register.submitButton')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
